import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export type BookEngine =
  | 'editor'
  | 'outline'
  | 'story-studio'
  | 'board'
  | 'world-builder'
  | 'consistency';

export interface BookEditorSessionDto {
  bookId: string;
  lastEngine: BookEngine | null;
  currentChapterId: string | null;
  updatedAt: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export function useBookEditorSession(bookId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<BookEditorSessionDto | null>({
    enabled: !!bookId,
    queryKey: ['book-editor-session', bookId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/books/${bookId}/editor-session`)
        .json<ApiEnvelope<BookEditorSessionDto | null>>();
      return resp.data;
    },
  });
}

export function useTrackLastEngine() {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (input: { bookId: string; engine: BookEngine }) => {
      const client = createApiClient(getToken);
      await client.put(
        `api/v2/books/${input.bookId}/editor-session/last-engine`,
        {
          json: { engine: input.engine },
        },
      );
    },
  });
}

/**
 * Hook called from each engine route — fires a single pn to persist the last-visited
 * engine. Safe to call on every render; the underlying mutation is cheap (returns 204)
 * and the useEffect ensures we only post once per (book, engine) pair.
 */
export function useMarkLastEngine(
  bookId: string | undefined,
  engine: BookEngine,
) {
  const track = useTrackLastEngine();
  useEffect(() => {
    if (!bookId) return;
    track.mutate({ bookId, engine });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, engine]);
}
